/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { forwardRef, useState, useEffect } from 'react';
// @mui
import { Box, Stack, Dialog, Button, AppBar, Typography, Slide, Tabs, Tab } from '@mui/material';
import moment from 'moment';
// components
import useAuth from 'src/hooks/useAuth';
import OverviewTab from 'src/sections/@dashboard/lead/OverviewTab';
import AttendedChecklistV2, { useAttendedChecklistVisibility } from 'src/sections/@dashboard/leadDetail/AttendedChecklistV2';
import NotesTab from 'src/sections/@dashboard/lead/NotesTab';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import { callApi } from 'src/utils/apiCall';
import { getChoicesList } from 'src/redux/slices/practiceTreatment';
import { ConsultationAttendedAppointment } from '../Attended';
import Iconify from '../Iconify';
import { ToDoList } from '../../sections/@dashboard/lead';
import { clearLead, getLeadNotes, getLeadReminderData, followUpSlice } from '../../redux/slices/lead';
import { setCurrentTreatmentDataGlobal } from '../../redux/slices/multiTreatment';
import { useSelector, dispatch } from '../../redux/store';
import {
    selectLeadFlowReminder,
    selectTaskFlowCounter,
    selectLeadNotes,
    selectLeadNotesArr,
} from '../../redux/selectors/leadSelectors';
import {
    commonDialogHeaderStyle,
    onBoardDialogHeader,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
} from '../../styles/ScheduleReminder';
import {
    rightBoxInnerStyle,
    leftBoxInnerStyle,
    tabsMainBoxStyle,
    tabButtonStyle,
    tabContentBoxStyle,
    listCounterBoxStyle,
    popupMainStackStyle,
} from '../../styles/ContactLeadPopup';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function OpenConsultationPopup({ selectedTreatmentData, handleReloadLeads, open, onClose, type = 'lead', user, inputRef, headerButtons, startTreatment, OpenAssignTask, editDetailHandler, handleCloseAndReload, defaultTab }) {
    const [sidebarTab, setSidebarTab] = useState('overview');
    // Bumps each time the sidebar is opened programmatically (e.g. follow-up
    // Email/WhatsApp redirect) so the glow overlay re-mounts and replays its
    // attention pulse — draws the user's eye to the composer they need to use.
    const [sidebarPulse, setSidebarPulse] = useState(0);
    const openSidebarTab = (v) => {
        setSidebarTab(v);
        setSidebarPulse((n) => n + 1);
    };
    const [treatmentData, setTreatmentData] = useState(null);
    const [notesLength, setNotesLength] = useState([]);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const leadFlowReminder = useSelector(selectLeadFlowReminder);
    const taskFlowCounter = useSelector(selectTaskFlowCounter);
    const leadNotes = useSelector(selectLeadNotes);
    const leadNotesArr = useSelector(selectLeadNotesArr);
    const followLead = useSelector((state) => state.lead.followLead);
    const { user: authUser } = useAuth();
    const isSuperuser = !!authUser?.is_superuser;

    const currentLeadId = user?.lead_id || user?.id;
    const leadDetailForChecklist = (followLead && Number(followLead.id) === Number(currentLeadId))
        ? followLead
        : { id: currentLeadId, lead_treatments: user?.lead_treatments || [] };
    const attendedVisibility = useAttendedChecklistVisibility({
        practiceId,
        leadId: currentLeadId,
        leadDetail: leadDetailForChecklist,
    });

    useEffect(() => {
        const currentLeadId = user?.lead_id || user?.id;
        if (!practiceId || !currentLeadId) return;
        setNotesLength([]);
        dispatch(getLeadNotes(currentLeadId, practiceId));
        dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'leadNotes', currentLeadId, '', '', '', 'counter', '', false, true));
        dispatch(getChoicesList(practiceId));
        dispatch(followUpSlice(currentLeadId, practiceId, 'LeadDetail'));
    }, [user?.id, user?.lead_id, practiceId]);

    useEffect(() => {
        const fetchData = async () => {
            const currentLeadId = user?.lead_id || user?.id;
            if (!practiceId || !currentLeadId) return;
            try {
                const resp = await callApi(`/api/v1/practices/${practiceId}/leads/${currentLeadId}/treatments`, 'GET');
                setTreatmentData(resp?.data?.results ?? []);
            } catch (error) {
                console.error('Error fetching treatment data:', error);
            }
        };
        fetchData();
    }, [practiceId, user?.id, user?.lead_id]);

    useEffect(() => {
        const combineNotes = [];
        if (leadNotes?.length > 0) {
            combineNotes.push(...leadNotes.filter((el) => el.additional_notes !== ''));
        }
        if (leadNotesArr?.results?.length > 0) {
            combineNotes.push(...leadNotesArr.results.filter((el) => el.note !== ''));
        }
        if (combineNotes.length) {
            setNotesLength(
                combineNotes.sort(
                    (a, b) =>
                        new Date(moment(a.updated_at, 'DD-MM-YYYY HH:mm:ss')) -
                        new Date(moment(b.updated_at, 'DD-MM-YYYY HH:mm:ss'))
                )
            );
        }
    }, [user, leadNotes, leadNotesArr]);

    const handleTabChange = (event, newValue) => setSidebarTab(newValue);

    const handleClose = async (action) => {
        if (action === 'tco') {
            return;
        }
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        onClose();
    };

    const tabCounter = (value) => {
        if (value === 'to-do') {
            return taskFlowCounter?.[0]?.count || 0;
        }
        return 0;
    };

    const labelCount = (data) => {
        if (data.label === 'To-do') {
            return (
                <Box>
                    {data.label}
                    <Box sx={listCounterBoxStyle}>
                        {tabCounter(data.value)}
                    </Box>
                </Box>
            );
        }
        return data.label;
    };

    const SIDEBAR_TABS = [
        {
            icon: 'grommet-icons:overview',
            label: 'Overview',
            value: 'overview',
            component: (
                <OverviewTab
                    treatmentTabData={treatmentData || []}
                    leadId={user?.lead_id || user?.id}
                    setExpandLayout={() => {}}
                    handleChange={handleTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={user}
                    LeadNotes={notesLength}
                    showDetailModes
                />
            ),
            width: 24,
            height: 24,
        },
        ...(attendedVisibility.visible ? [{
            icon: 'mdi:checkbox-marked-outline',
            label: 'Attended Checklist',
            value: 'checklist',
            component: (
                <AttendedChecklistV2
                    practiceId={practiceId}
                    leadId={currentLeadId}
                    leadDetail={leadDetailForChecklist}
                />
            ),
            width: 20,
            height: 20,
        }] : []),
        {
            icon: 'bi:check-circle-fill',
            label: 'To-do',
            value: 'to-do',
            component: (
                <ToDoList
                    setExpandLayout={() => {}}
                    handleChange={handleTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={user}
                    isForTab
                />
            ),
            width: 18,
            height: 18,
        },
        {
            icon: 'solar:chat-round-dots-bold',
            label: 'Notes',
            value: 'notes',
            component: (
                <NotesTab
                    LeadNotes={notesLength}
                    isForTab
                    userDetails={user}
                    handleChange={handleTabChange}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'mdi:email-outline',
            label: 'Email',
            value: 'email',
            component: (
                <IosEmailComposerBody
                    leadId={currentLeadId}
                    practiceId={practiceId}
                    leadDetail={followLead || user}
                    compact
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'ic:baseline-whatsapp',
            label: 'WhatsApp',
            value: 'whatsapp',
            component: (
                <FollowUpWhatsappChat
                    user={followLead || user}
                    followLead={followLead}
                />
            ),
            width: 20,
            height: 20,
        },
        ...(isSuperuser ? [{
            icon: 'solar:bug-bold-duotone',
            label: 'Debug',
            value: 'debug',
            component: (
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>
                        V2 Visibility (superadmin)
                    </Typography>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '11px', color: '#1d1d1f', bgcolor: '#F5F5F7', p: 1.5, borderRadius: '8px', lineHeight: 1.7 }}>
                        <div>practiceId: {String(practiceId)}</div>
                        <div>leadId: {String(currentLeadId)}</div>
                        <div>ready: {String(attendedVisibility?.ready)}</div>
                        <div>leadInAttended: {String(attendedVisibility?.leadInAttended)}</div>
                        <div>hasAttendedTask: {String(attendedVisibility?.hasAttendedTask)}</div>
                        <div>visible: {String(attendedVisibility?.visible)}</div>
                        <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                        <div>featureAccess: {String(attendedVisibility?.debug?.featureAccess)}</div>
                        <div>groupsCount: {String(attendedVisibility?.debug?.groupsCount ?? '—')}</div>
                        <div>templatesCount: {String(attendedVisibility?.debug?.templatesCount ?? '—')}</div>
                        <div>attendedTemplatesCount: {String(attendedVisibility?.debug?.attendedTemplatesCount ?? '—')}</div>
                        <div>matchingGroupsCount: {String(attendedVisibility?.debug?.matchingGroupsCount ?? '—')}</div>
                        <div>groupsWithItemsCount: {String(attendedVisibility?.debug?.groupsWithItemsCount ?? '—')}</div>
                        {attendedVisibility?.debug?.error && (
                            <div style={{ color: '#FF5630', marginTop: 4 }}>error: {String(attendedVisibility.debug.error)}</div>
                        )}
                        {attendedVisibility?.debug?.sampleTask && (
                            <>
                                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                                <div>sampleTask.id: {String(attendedVisibility.debug.sampleTask.id)}</div>
                                <div>sampleTask.title: {String(attendedVisibility.debug.sampleTask.title)}</div>
                                <div>sampleTask.keys: [{(attendedVisibility.debug.sampleTask.keys || []).join(', ')}]</div>
                                <div>sampleTask.checklist_items.length: {String(attendedVisibility.debug.sampleTask.checklist_items_len)}</div>
                            </>
                        )}
                    </Box>
                </Box>
            ),
            width: 18,
            height: 18,
        }] : []),
    ];

    return (
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            sx={[reminderDialogStyle(type), { pt: { xs: 0, sm: 0 }, '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' } }]}
            disableEnforceFocus
        >
            <AppBar sx={[headerButtons ? commonDialogHeaderStyle : onBoardDialogHeader, { position: 'sticky !important', top: 0, alignItems: 'center' }]}>
                <Typography
                    sx={reminderTitleStyle}
                    variant="h6"
                    component="div"
                >
                    Attended consultation for <span style={{ textTransform: 'capitalize' }}>{user?.first_name}</span>
                </Typography>
                {(() => {
                    const followLeadFilterAttend = followLead?.lead_appointment?.filter((el) => el.status === 'ATTENDED') || [];
                    const apptDt = (selectedTreatmentData?.treatmentData?.id
                        ? followLead?.lead_appointment?.find(e => e?.lead_treatment === selectedTreatmentData.treatmentData.id)?.datetime
                        : null) || followLeadFilterAttend[0]?.datetime;
                    if (!apptDt) return null;
                    const parsed = moment(apptDt, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', moment.ISO_8601], true);
                    if (!parsed.isValid()) return null;
                    const days = moment().startOf('day').diff(parsed.startOf('day'), 'days');
                    const contactedCount = followLead?.lead_contacted_counts?.find(c => c.status === 'CONSULTATION_ATTENDED')?.count || 0;
                    const followUpStep = contactedCount + 1;
                    return (
                        <Box sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            gap: 1.5,
                            '@keyframes consultPulse': {
                                '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255,255,255,0.4)' },
                                '50%': { transform: 'scale(1.25)', boxShadow: '0 0 0 4px transparent' },
                                '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 transparent' },
                            },
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    animation: 'consultPulse 2s ease-in-out infinite',
                                    flexShrink: 0,
                                }}>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        color: '#007867',
                                        lineHeight: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}>
                                        {followUpStep}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>
                                    Follow up {followUpStep}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '1px', height: '16px', bgcolor: 'rgba(255,255,255,0.30)' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    animation: 'consultPulse 2s ease-in-out infinite',
                                    flexShrink: 0,
                                }} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                    {(() => {
                                        if (days === 0) return 'Today';
                                        if (days === 1) return '1 day ago';
                                        return `${days} days ago`;
                                    })()}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })()}
                {headerButtons &&
                    <Box sx={cancelBoxStyle}>
                        <Button
                            color="inherit"
                            variant='outlined'
                            style={{ borderColor: '#FFFFFF', color: '#fff' }}
                            onClick={() => { handleClose('close'); }}
                        >
                            Cancel
                        </Button>
                    </Box>
                }
            </AppBar>
            <Stack spacing={0} sx={[popupMainStackStyle(sidebarTab), { width: sidebarTab === '' ? 'calc(100% - 85px)' : 'calc(100% - 506px)', pb: '88px' }]}>
                {/* Left side - main content */}
                <Box sx={[leftBoxInnerStyle(false, true), { background: '#F5F5F7' }]}>
                    <ConsultationAttendedAppointment
                        selectedTreatmentData={selectedTreatmentData}
                        handleReloadLeads={handleReloadLeads}
                        handleCloseAndReload={handleCloseAndReload}
                        handleClose={handleClose}
                        user={user}
                        type='Starting treatment'
                        inputRef={inputRef}
                        startTreatment={startTreatment}
                        OpenAssignTask={OpenAssignTask}
                        editDetailHandler={editDetailHandler}
                        defaultTab={defaultTab}
                        onOpenSidebarTab={openSidebarTab}
                    />
                </Box>
                {/* Right side - sidebar with tabs */}
                <Box sx={[rightBoxInnerStyle(false, sidebarTab), { top: '88px', height: 'calc(100vh - 152px)' }]}>
                    {/* Attention pulse — re-mounts on each programmatic open (keyed by
                        sidebarPulse) so the glow ring replays and draws the eye to the
                        composer. pointerEvents:none so it never blocks interaction. */}
                    {sidebarPulse > 0 && (
                        <Box
                            key={sidebarPulse}
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 5,
                                pointerEvents: 'none',
                                borderRadius: 'inherit',
                                '@keyframes sidebarAttentionPulse': {
                                    '0%':   { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0.55)', opacity: 1 },
                                    '60%':  { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0.55)', opacity: 1 },
                                    '100%': { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0)',    opacity: 0 },
                                },
                                animation: 'sidebarAttentionPulse 1.6s ease-out forwards',
                            }}
                        />
                    )}
                    {SIDEBAR_TABS.map((tab) => {
                        const isMatched = tab.value === sidebarTab;
                        return isMatched && (
                            <Box key={tab.value} sx={tabContentBoxStyle}>
                                {tab.component}
                            </Box>
                        );
                    })}
                    <Box sx={tabsMainBoxStyle}>
                        <Tabs
                            orientation='vertical'
                            variant='fullWidth'
                            value={sidebarTab}
                            onChange={handleTabChange}
                            sx={{ '.MuiTabs-flexContainer': { gap: 1 } }}
                        >
                            {SIDEBAR_TABS.map((tab, index) => (
                                <Tab
                                    icon={
                                        <Iconify
                                            icon={tab.icon}
                                            width={tab.width}
                                            height={tab.height}
                                            sx={{ mr: '0 !important', mb: '4px' }}
                                        />
                                    }
                                    key={index}
                                    label={labelCount(tab)}
                                    value={tab.value}
                                    sx={tabButtonStyle}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>
            </Stack>
        </Dialog>
    );
}

OpenConsultationPopup.propTypes = {
    handleReloadLeads: PropTypes.func,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object,
    inputRef: PropTypes.any,
    headerButtons: PropTypes.bool,
    startTreatment: PropTypes.string,
    OpenAssignTask: PropTypes.any,
    editDetailHandler: PropTypes.any,
    handleCloseAndReload: PropTypes.func,
    selectedTreatmentData: PropTypes.any,
    defaultTab: PropTypes.string,
};
